import { useEffect, useMemo } from 'react'

import { useOrganizationId } from '../../../../hooks/useCurrentOrganization'
import { usePlanSelectorQuery } from '../../../PlanSelector/hooks/usePlanSelectorQuery'
import {
  calculateTierPrice,
  calculateYearlySavings,
  formatPrice,
} from '../../../PlanSelector/utils/pricingCalculations'
import { BillingIntervalValues } from '../../../PlanSelector/types'
import type { ObPlanId, BillingInterval } from '../../../../gql/graphql'
import { useGetAnnualUpgradePreviewInvoice } from './useGetAnnualUpgradePreviewInvoice'

interface AnnualUpgradeData {
  planId: ObPlanId
  planName: string
  quantity: number
  currency: string
  monthlyPricePerMonth: string
  monthlyPricePerYear: string
  annualPricePerYear: string
  savingsAmount: string
  savingsMonths: number
  dueNow: string
  /** Next annual renewal after switching (preview API period start + 1 year). */
  nextBillingDate: string
  currentInterval: BillingInterval
}

interface UseAnnualUpgradeDataReturn {
  data: AnnualUpgradeData | null
  loading: boolean
  invoiceLoading: boolean
  error: boolean
  refetch: () => Promise<unknown>
}

export function useAnnualUpgradeData(): UseAnnualUpgradeDataReturn {
  const organizationId = useOrganizationId()
  const {
    data: planData,
    loading: planLoading,
    error: planError,
    refetch,
  } = usePlanSelectorQuery()

  const {
    fetchAnnualUpgradePreview,
    data: invoiceData,
    loading: invoiceLoading,
    error: invoiceError,
    clearData,
  } = useGetAnnualUpgradePreviewInvoice({ showErrorToast: false })

  const subscription =
    planData?.__typename === 'PlanSelectorData' ? planData.subscription : null
  const availablePlans =
    planData?.__typename === 'PlanSelectorData' ? planData.availablePlans : null

  const currentPlanId = subscription?.plan?.id
  const currentInterval = subscription?.interval
  const quantity = subscription?.quantity ?? 0

  // Find the current monthly plan and corresponding annual plan
  const monthlyPlan = useMemo(
    () =>
      availablePlans?.find(
        (p) =>
          p.planId === currentPlanId &&
          p.planInterval === BillingIntervalValues.month,
      ),
    [availablePlans, currentPlanId],
  )

  const annualPlan = useMemo(
    () =>
      availablePlans?.find(
        (p) =>
          p.planId === currentPlanId &&
          p.planInterval === BillingIntervalValues.year,
      ),
    [availablePlans, currentPlanId],
  )

  const shouldFetchInvoice = !!(
    organizationId &&
    currentPlanId &&
    annualPlan &&
    currentInterval === BillingIntervalValues.month
  )

  useEffect(() => {
    if (!shouldFetchInvoice || !organizationId) {
      clearData()
      return
    }
    void fetchAnnualUpgradePreview(organizationId)
  }, [shouldFetchInvoice, organizationId, fetchAnnualUpgradePreview, clearData])

  const data = useMemo((): AnnualUpgradeData | null => {
    if (
      !subscription ||
      !monthlyPlan ||
      !annualPlan ||
      !currentPlanId ||
      currentInterval !== BillingIntervalValues.month
    ) {
      console.warn('[AnnualUpgrade] data is null:', {
        hasSubscription: !!subscription,
        hasMonthlyPlan: !!monthlyPlan,
        hasAnnualPlan: !!annualPlan,
        currentPlanId,
        currentInterval,
        expectedInterval: BillingIntervalValues.month,
      })
      return null
    }

    const currency = monthlyPlan.pricing[0]?.currency ?? 'USD'

    const monthlyCalc = calculateTierPrice(monthlyPlan.pricing, quantity)
    const annualCalc = calculateTierPrice(annualPlan.pricing, quantity)

    const monthlyTotalPerYear = monthlyCalc.totalPrice * 12
    const savings = calculateYearlySavings(
      monthlyCalc.totalPrice,
      annualCalc.totalPrice,
    )

    const savingsMonths =
      monthlyCalc.totalPrice > 0
        ? Math.round(savings / monthlyCalc.totalPrice)
        : 0

    return {
      planId: currentPlanId,
      planName: monthlyPlan.planName,
      quantity,
      currency,
      monthlyPricePerMonth: formatPrice(monthlyCalc.totalPrice, currency),
      monthlyPricePerYear: formatPrice(monthlyTotalPerYear, currency),
      annualPricePerYear: formatPrice(annualCalc.totalPrice, currency),
      savingsAmount: formatPrice(savings, currency),
      savingsMonths,
      dueNow: invoiceData ? formatPrice(invoiceData.dueNow, currency) : '',
      nextBillingDate: invoiceData?.nextBillingDate ?? '',
      currentInterval,
    }
  }, [
    subscription,
    monthlyPlan,
    annualPlan,
    currentPlanId,
    currentInterval,
    quantity,
    invoiceData,
  ])

  return {
    data,
    loading: !organizationId || planLoading,
    invoiceLoading,
    error: !!planError || !!invoiceError,
    refetch,
  }
}
